import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { ArrowsClockwise, MagnifyingGlass, Trash, Warning, X } from "@phosphor-icons/react";

const LEVEL_COLORS = {
  WARNING: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-500" },
  ERROR: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", dot: "bg-red-500" },
  CRITICAL: { bg: "bg-red-100", border: "border-red-300", text: "text-red-800", dot: "bg-red-700" },
  INFO: { bg: "bg-zinc-50", border: "border-zinc-200", text: "text-zinc-700", dot: "bg-zinc-500" },
};

function formatAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m ago`;
  if (ms < 86400000) return `${Math.floor(ms / 3600000)}h ago`;
  return `${Math.floor(ms / 86400000)}d ago`;
}

export default function LogsPage() {
  const [logs, setLogs] = useState([]);
  const [counts, setCounts] = useState({});
  const [loggers, setLoggers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [level, setLevel] = useState("ALL");
  const [loggerName, setLoggerName] = useState("ALL");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [expanded, setExpanded] = useState({});

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (level !== "ALL") params.level = level;
      if (loggerName !== "ALL") params.logger_name = loggerName;
      if (search) params.search = search;
      const { data } = await api.get("/admin/logs", { params });
      setLogs(data.logs || []);
      setCounts(data.counts || {});
      setLoggers(data.loggers || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load logs");
    }
    setLoading(false);
  }, [level, loggerName, search]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const clearAll = async () => {
    if (!window.confirm("Delete ALL captured log entries? This cannot be undone.")) return;
    try {
      const { data } = await api.delete("/admin/logs");
      toast.success(data.message);
      fetchLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const runSearch = () => setSearch(searchInput.trim());

  return (
    <div className="space-y-6" data-testid="logs-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-900">Application Logs</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Last 14 days of warnings/errors captured from the running backend.
            Useful for diagnosing webhook failures, integration errors, and
            background sync issues without shell access.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchLogs} variant="outline" data-testid="logs-refresh-btn" className="rounded-sm text-xs gap-1.5">
            <ArrowsClockwise size={12} weight="bold" /> Refresh
          </Button>
          <Button onClick={clearAll} variant="outline" data-testid="logs-clear-btn"
            className="rounded-sm text-xs gap-1.5 border-red-200 text-red-600 hover:bg-red-50">
            <Trash size={12} /> Clear all
          </Button>
        </div>
      </div>

      {/* Tally */}
      <div className="flex flex-wrap gap-2">
        {["WARNING", "ERROR", "CRITICAL"].map((l) => (
          <Badge key={l} className={`text-[10px] uppercase tracking-wider rounded-sm font-semibold ${
            LEVEL_COLORS[l].bg
          } ${LEVEL_COLORS[l].text} border ${LEVEL_COLORS[l].border}`}>
            {l}: {counts[l] || 0}
          </Badge>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center bg-white border border-zinc-200 rounded-sm p-3">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Level</span>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger data-testid="logs-level-filter" className="h-8 w-32 text-xs rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All</SelectItem>
              <SelectItem value="WARNING">Warning</SelectItem>
              <SelectItem value="ERROR">Error</SelectItem>
              <SelectItem value="CRITICAL">Critical</SelectItem>
              <SelectItem value="ERROR,CRITICAL">Error+Critical</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Source</span>
          <Select value={loggerName} onValueChange={setLoggerName}>
            <SelectTrigger data-testid="logs-source-filter" className="h-8 w-40 text-xs rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All sources</SelectItem>
              {loggers.map((l) => (
                <SelectItem key={l} value={l}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Input
            data-testid="logs-search-input"
            placeholder="Search message text…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            className="h-8 rounded-sm text-xs"
          />
          <Button onClick={runSearch} variant="outline" data-testid="logs-search-btn" className="rounded-sm text-xs gap-1 h-8">
            <MagnifyingGlass size={12} /> Search
          </Button>
          {search && (
            <Button onClick={() => { setSearch(""); setSearchInput(""); }} variant="ghost"
              className="rounded-sm text-xs gap-1 h-8 text-zinc-500">
              <X size={12} /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-zinc-400 text-sm">Loading logs…</div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-zinc-400 text-sm" data-testid="logs-empty">
          No log entries match the current filter.
        </div>
      ) : (
        <div className="space-y-1.5">
          {logs.map((log, idx) => {
            const c = LEVEL_COLORS[log.level] || LEVEL_COLORS.INFO;
            const k = `${log.at}-${idx}`;
            const isExpanded = !!expanded[k];
            return (
              <div
                key={k}
                data-testid={`log-entry-${idx}`}
                className={`rounded-sm border ${c.border} ${c.bg} px-3 py-2 cursor-pointer hover:shadow-sm transition-shadow`}
                onClick={() => setExpanded((p) => ({ ...p, [k]: !p[k] }))}
              >
                <div className="flex items-start gap-2">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${c.dot} mt-1.5 flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-[10px] text-zinc-500 mb-0.5">
                      <span className={`uppercase font-semibold ${c.text}`}>{log.level}</span>
                      <span>•</span>
                      <span className="font-mono">{log.logger}</span>
                      <span>•</span>
                      <span className="font-mono">{log.module}:{log.line}</span>
                      <span>•</span>
                      <span title={log.at}>{formatAgo(log.at)}</span>
                    </div>
                    <div className={`text-xs font-mono ${c.text} ${isExpanded ? "" : "truncate"}`}>
                      {log.message}
                    </div>
                    {isExpanded && log.exc && (
                      <pre className="mt-2 text-[10px] bg-zinc-900 text-zinc-100 p-2 rounded-sm overflow-x-auto font-mono whitespace-pre-wrap">
                        {log.exc}
                      </pre>
                    )}
                    {isExpanded && (
                      <div className="mt-1.5 text-[10px] text-zinc-500 flex gap-3">
                        <span><span className="text-zinc-400 uppercase tracking-wider">Func:</span> <span className="font-mono">{log.func}</span></span>
                        <span><span className="text-zinc-400 uppercase tracking-wider">Timestamp:</span> <span className="font-mono">{log.at}</span></span>
                      </div>
                    )}
                  </div>
                  {log.exc && <Warning size={12} className={c.text} weight="fill" />}
                </div>
              </div>
            );
          })}
          {logs.length >= 200 && (
            <div className="text-center text-[11px] text-zinc-500 italic py-3">
              Showing latest 200 entries. Refine filters to see more.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
